import { useTranslation } from "react-i18next";
import AsyncButton from "@/components/AsyncButton";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, MessageSquare } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAgreementContext } from "../../context";
import { AgreementCommentDialog } from "../shared";
import { useState } from "react";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { CommentItem } from "../../context/AgreementContext";

export const AgreementApprovalSection = () => {
  const { t } = useTranslation();
  const {
    handleConfirm,
    handleReject,
    handleComment,
    transitions,
    comments,
    loadingState,
    hasValidComments,
  } = useAgreementContext();
  const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false);

  const handleCommentSubmit = async (dialogComments: CommentItem[]) => {
    await handleComment(dialogComments);
  };

  if (!transitions.canPerformVerificationActions) {
    return null;
  }

  return (
    <>
      <CollapsibleSection title={t("agreements.approval.title")}>
        <div className="flex gap-3">
          {transitions.canConfirm && (
            <AsyncButton
              onClick={handleConfirm}
              className="bg-green-600 hover:bg-green-700"
              label={t("agreements.approval.confirm")}
              icon={<CheckCircle className="h-4 w-4" />}
              loading={
                loadingState.isLoading &&
                loadingState.reason === "confirming_agreement"
              }
            />
          )}

          {transitions.canReject && (
            <AsyncButton
              onClick={handleReject}
              variant="destructive"
              label={t("agreements.approval.reject")}
              icon={<XCircle className="h-4 w-4" />}
              loading={
                loadingState.isLoading &&
                loadingState.reason === "rejecting_agreement"
              }
            />
          )}

          {transitions.canComment && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Button
                      onClick={() => setIsCommentDialogOpen(true)}
                      variant="outline"
                      disabled={!hasValidComments}
                    >
                      <MessageSquare className="h-4 w-4" />
                      {t("agreements.approval.comment")}
                    </Button>
                  </div>
                </TooltipTrigger>
                {!hasValidComments && (
                  <TooltipContent>
                    <p>{t("agreements.approval.commentTooltip")}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </CollapsibleSection>

      <AgreementCommentDialog
        open={isCommentDialogOpen}
        onOpenChange={setIsCommentDialogOpen}
        onConfirm={handleCommentSubmit}
        isPending={
          loadingState.isLoading &&
          loadingState.reason === "requesting_comments"
        }
        initialComments={comments}
      />
    </>
  );
};
